import React, { useEffect, useState } from "react";
import { Calendar, Clock, MapPin, Heart, Leaf, Users, ArrowRight, CheckCircle, Star, Phone, Mail, Globe, Flower2, BookOpen, Coffee, Home, Brain, Sparkles, Compass, Shield, MessageCircle, Target, Bed, Apple } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import NavBar from '@/components/NavBar';
import { ReturnToTop } from '@/components/ReturnToTop';
import ScrollDownArrow from '@/components/ScrollDownArrow';
import { BOOKING_URL } from '@/constants/urls';
import youngAdultImageWebP from '@/assets/young_adult.webp';
import youngAdultImage from '@/assets/young_adult.jpg';

const YoungAdultsPage = () => {
  const [isLoading, setIsLoading] = useState(false);

  // Force scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Add dynamic meta tags and structured data
  useEffect(() => {
    // Update document title and meta tags
    document.title = "Chinese Medicine for Young Adults Sunshine Coast | Life Balance & Wellness";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Feel grounded, clear, and connected. Chinese medicine support for young adults navigating life's transitions, stress, and hormonal balance in Currimundi.");
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Chinese Medicine for Young Adults Sunshine Coast | Life Balance & Wellness");
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Feel grounded, clear, and connected. Chinese medicine support for young adults navigating life's transitions, stress, and hormonal balance in Currimundi.");
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/young-adults");
    }

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', "https://womenandtheway.com.au/young-adult.webp");
    }

    // Update Twitter Card
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Chinese Medicine for Young Adults Sunshine Coast | Life Balance & Wellness");
    }

    // Add LocalBusiness Schema for young adult services
    const youngAdultsSchema = {
      "@context": "https://schema.org",
      "@type": "HealthAndBeautyBusiness",
      "name": "Women and The Way - Chinese Medicine for Young Adults",
      "image": "https://womenandtheway.com.au/young-adult.webp",
      "description": "Feel grounded, clear, and connected. Chinese medicine support for young adults navigating life's transitions, stress, and hormonal balance in Currimundi.",
      "url": "https://womenandtheway.com.au/young-adults",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "700 Nicklin Way",
        "addressLocality": "Currimundi",
        "addressRegion": "QLD",
        "addressCountry": "AU"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -26.5270,
        "longitude": 153.0890
      },
      "priceRange": "$$",
      "serviceType": "Young Adult Health, Acupuncture, Chinese Medicine, Life Balance",
      "sameAs": [
        "https://www.facebook.com/Womenandtheway",
        "https://www.instagram.com/women_and_the_way"
      ]
    };

    // Remove existing schema if any
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    // Add new schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(youngAdultsSchema);
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const handleBooking = async () => {
    setIsLoading(true);
    try {
      window.open(BOOKING_URL, '_blank');
    } catch (error) {
      console.error('Booking error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-amber-50">
        <NavBar />
        
        {/* Hero Section */}
        <section className="relative py-20 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                  Chinese Medicine for Young Adults
                </h1>
                <p className="text-xl md:text-2xl text-sage-600 mb-6 font-medium">
                  Feel grounded, clear, and connected - wherever life is taking you.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Graduating, finding your feet, applying for jobs, swiping right, signing leases (or avoiding them), figuring out what you want… this stage of life is exciting - but it can also feel overwhelming, unstructured, or like everyone else has it figured out.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  At Women and the Way, we support young adults navigating the flux of their twenties with the grounding tools of Chinese medicine and acupuncture. Whether you're chasing big goals or feeling a little lost, we're here to help you find balance - physically, emotionally, and energetically.
                </p>
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto text-center break-words !whitespace-normal bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 !flex !flex-col sm:!flex-row items-center justify-center gap-2 mx-auto min-w-0 min-h-[60px] sm:min-h-[auto]"
                  onClick={handleBooking}
                  disabled={isLoading}
                >
                  <span className="block break-words whitespace-normal text-center">{isLoading ? 'Booking...' : 'Book Now'}</span>
                  <ArrowRight className="w-5 h-5 ml-2 flex-shrink-0" />
                </Button>
              </div>
              <div className="relative">
                <picture>
                  <source srcSet={youngAdultImageWebP} type="image/webp" />
                  <img 
                    src={youngAdultImage} 
                    alt="Young adult receiving acupuncture for life balance and wellness" 
                    className="rounded-2xl shadow-lg w-full h-auto"
                  />
                </picture>
              </div>
            </div>
          </div>
        </section>

        {/* Why Chinese Medicine for Young Adults */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 flex items-center justify-center gap-3 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                <Compass className="w-8 h-8 text-sage-600" />
                Why Chinese Medicine for Young Adults?
              </h2>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12">
              <p className="text-lg text-sage-700 mb-6 leading-relaxed">
                In Chinese medicine, the twenties are a key time to nourish your foundation. You're still building your Qi and Blood, solidifying your constitutional strengths, and learning how stress, sleep, food, relationships, and hormonal patterns affect your whole system.
              </p>
              <p className="text-lg text-sage-700 mb-6 leading-relaxed">
                But in the hustle of modern life, it's easy to burn out - especially with irregular hours, emotional intensity, and little time for rest and repair. That's where we come in.
              </p>
              <p className="text-lg text-sage-700 leading-relaxed">
                Chinese medicine offers a refreshing alternative: natural, preventative care that helps you stay well, regulate cycles, support your skin, and manage emotional ups and downs.
              </p>
            </div>
          </div>
        </section>

        {/* What We Offer */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                What We Offer
              </h2>
              <p className="text-xl text-sage-600 mb-8">
                Support for Young Adults In-Clinic or Online
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Home className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">In-Clinic Sessions</h3>
                  </div>
                  <p className="text-sage-700 mb-4">
                    Come to our peaceful Currimundi clinic for sessions tailored to the energy, pace, and pressures of your life right now.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🪡</span>
                      <p className="text-sage-700">Acupuncture for stress relief, period support, skin balance, and sleep regulation</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌿</span>
                      <p className="text-sage-700">Chinese herbal formulas for hormonal fluctuations, digestion, emotional stability, and more</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌀</span>
                      <p className="text-sage-700">Nervous system reset for burnout, anxious thoughts, or feeling stuck</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">👥</span>
                      <p className="text-sage-700">Life-stage support for the "in between" years - career, fertility, identity, relationships</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Globe className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Online Sessions</h3>
                  </div>
                  <p className="text-sage-700 mb-4 font-medium">
                    Can't make it in person? No worries - we've got you.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">💬</span>
                      <p className="text-sage-700">Telehealth consults to discuss symptoms, emotions, lifestyle, and menstrual cycles</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🌿</span>
                      <p className="text-sage-700">Custom herbal prescriptions posted to your door or ready for clinic pick-up</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🗺️</span>
                      <p className="text-sage-700">Clear, kind guidance for creating new rhythms, rituals, and routines</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Common Themes We Support */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                Common Themes We Support
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { western: "Menstrual pain & irregular cycles", chinese: "Liver Qi stagnation, Blood deficiency" },
                { western: "Acne, skin flares & bloating", chinese: "Heat in Stomach channel, Dampness" },
                { western: "Stress, burnout & emotional overwhelm", chinese: "Heart/Spleen imbalance, Liver Qi constraint" },
                { western: "Sleep issues & fatigue", chinese: "Yin deficiency, Spleen Qi deficiency" },
                { western: "Libido changes & sexual health", chinese: "Kidney Yin/Yang deficiency, Heart-Liver disconnect" },
                { western: "Anxiety, overthinking, indecision", chinese: "Heart Blood deficiency, Spleen Qi deficiency" },
                { western: "Coming off the Pill", chinese: "Liver Qi constraint, Blood deficiency" },
                { western: "Digestive ups & downs", chinese: "Spleen/Stomach disharmony" }
              ].map((condition, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-sage-800 mb-2">{condition.western}</h3>
                    <p className="text-sage-600 text-sm italic">{condition.chinese}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What Makes Us Different */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                What Makes Our Approach Different
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { icon: "🫶", title: "No one-size-fits-all care", desc: "Your treatments, herbal formulas, and rhythm support are tailored to your unique pattern and lifestyle" },
                { icon: "🌿", title: "Natural & empowering", desc: "Our goal is to strengthen your body's own ability to regulate and heal" },
                { icon: "📚", title: "Guidance without overwhelm", desc: "We keep it simple and practical - no perfection required" },
                { icon: "💬", title: "We've been there", desc: "Our team knows how chaotic this life stage can feel - and we're here to meet you with kindness and honesty" }
              ].map((feature, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl mb-3">{feature.icon}</div>
                    <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">{feature.title}</h3>
                    <p className="text-sage-700">{feature.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Support Tools */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                Support Tools We Offer in Clinic
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Leaf, title: "Custom Chinese herbal formulas", desc: "For cycle health, emotional wellbeing, and energy" },
                { icon: Coffee, title: "Hormone-balancing teas", desc: "And digestive blends for daily support" },
                { icon: Heart, title: "Castor oil packs", desc: "And heat therapy guidance for home use" },
                { icon: BookOpen, title: "Seed cycling kits", desc: "And supplement suggestions for hormone balance" },
                { icon: BookOpen, title: "Recommended reads", desc: "And ritual planning guides for lifestyle support" }
              ].map((tool, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <tool.icon className="w-8 h-8 text-sage-600 mx-auto mb-3" />
                    <h3 className="font-serif text-lg font-semibold text-sage-800 mb-2">{tool.title}</h3>
                    <p className="text-sage-700 text-sm">{tool.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
              Book Your Session
            </h2>
            <p className="text-xl text-sage-700 mb-8">
              Whether you're stepping into a new job, healing from burnout, navigating your cycle, or just need someone to help you reset - Chinese medicine can hold you through it. Let's restore your rhythm and bring you back to yourself.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <MapPin className="w-5 h-5 text-sage-600" />
                <a 
                  href="https://maps.google.com/?q=700+Nicklin+Way+Currimundi+QLD" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-sage-800 transition-colors"
                >
                  700 Nicklin Way, Currimundi
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Phone className="w-5 h-5 text-sage-600" />
                <a 
                  href="tel:0415537784" 
                  className="hover:text-sage-800 transition-colors"
                >
                  0415 537 784
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Mail className="w-5 h-5 text-sage-600" />
                <a 
                  href="mailto:womenandtheway@gmail.com" 
                  className="hover:text-sage-800 transition-colors"
                >
                  womenandtheway@gmail.com
                </a>
              </div>
            </div>
            
            <Button 
              size="lg" 
              className="w-full sm:w-auto text-center break-words !whitespace-normal bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 !flex !flex-col sm:!flex-row items-center justify-center gap-2 mx-auto min-w-0 min-h-[60px] sm:min-h-[auto]"
              onClick={handleBooking}
              disabled={isLoading}
            >
              <span className="block break-words whitespace-normal text-center">{isLoading ? 'Booking...' : 'Support for the in-between years - because feeling stuck doesn\'t mean you are.'}</span>
              <ArrowRight className="w-5 h-5 ml-2 flex-shrink-0" />
            </Button>
          </div>
        </section>

        <ScrollDownArrow />
        <ReturnToTop />
      </div>
    </>
  );
};

export default YoungAdultsPage; 